import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { SumUpDevReader } from '../../../terminals/core/sumup-dev-reader';
import { BroadcastChannelMessageTypes } from '../../../../core/enums/BroadcastChannelMessageTypes';
import { ApiErrorCodes, ErrorCodes } from '../../../../core/enums/ErrorCodes';
import { IResponse } from '../../../../core/interfaces/IResponse';
import { ApiService } from '../../../../core/services/ApiService';
import { BroadcastChannelService } from '../../../../core/services/BroadcastChannelService';
import { LocalStorageService } from '../../../../core/services/LocalStorageService';
import { NotificationService } from '../../../../core/services/NotificationService';
import { AddItemsToCart, RemoveItemsFromCart } from '../../../../core/types';
import { ShowsService } from '../../../shows/core/services/ShowsService';
import { TerminalsStore } from '../../../terminals/core/stores/TerminalsStore';
import { CartItemTypes } from '../enums/CartItemTypes';
import { DiscountType } from '../enums/DiscountTypes';
import { ICardPaymentData } from '../interfaces/ICardPaymentData';
import { ICart } from '../interfaces/ICart';
import { ICartAction } from '../interfaces/ICartAction';
import { ICartCheckout } from '../interfaces/ICartCheckout';
import { ICartItem } from '../interfaces/ICartItem';
import { ICartPayment } from '../interfaces/ICartPayment';
import { IReservationCode } from '../interfaces/IReservationCode';
import { CartStore } from '../stores/CartStore';
import { PaySeparatelyStore } from '../stores/PaySeparatelyStore';

interface CartErrorNotificationOptions {
  suppressedMessages?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CartService {

  private readonly _basePath = '/basket';
  private readonly _apiService = inject(ApiService);
  private readonly _terminalsStore = inject(TerminalsStore);
  private readonly _cartStore = inject(CartStore);
  private readonly _notificationService = inject(NotificationService);
  private readonly _localStorageService = inject(LocalStorageService);
  private readonly _broadcastChannelService = inject(BroadcastChannelService);
  private readonly _paySeparatelyStore = inject(PaySeparatelyStore);
  private readonly _showsService = inject(ShowsService);

  private static isCancelItemType(itemType: CartItemTypes): boolean {
    return (
      itemType === CartItemTypes.CancelTicket ||
      itemType === CartItemTypes.CancelVoucher ||
      itemType === CartItemTypes.CancelProduct ||
      itemType === CartItemTypes.CancelPackage ||
      itemType === CartItemTypes.CancelCard
    );
  }

  public getCart(): Observable<IResponse<ICart>> {
    return this._apiService.get(
      `${this._basePath}/${this._terminalsStore.selectedTerminal().currentSession.currentBasketId}`
    ).pipe(
      catchError(() => this.createCart(false)),
      tap((res: IResponse<ICart>) => this._cartStore.cart.set(res.data)),
    );
  }

  public getReservationCode(): Observable<IResponse<IReservationCode>> {
    return this._apiService.get(`${this._basePath}/reservation-code`);
  }

  public createCart(isAfterTransaction: boolean): Observable<IResponse<ICart>> {
    return this._apiService.post(this._basePath)
      .pipe(
        tap((res: IResponse<ICart>) => {
          this.clearPaySeparatelyMode();

          this._terminalsStore.selectedTerminal.update(terminal => ({
            ...terminal,
            currentSession: {
              ...terminal.currentSession,
              currentBasketId: res.data.id
            }
          }));

          this.triggerCartAction(res.data, isAfterTransaction ? 'createCartAfterTransaction' : 'createCart');
        })
      );
  }

  public addItemToCart(items: AddItemsToCart, notificationOptions?: CartErrorNotificationOptions): Observable<IResponse<ICart>> {
    if (
      this._paySeparatelyStore.isPaySeparatelyModeEnabled() &&
      items.some(item => item.itemType != null && CartService.isCancelItemType(item.itemType))
    ) {
      this._notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        ApiErrorCodes.PartialCheckoutNotAllowedWithCancelItems.trans(`errors/notifications/${ApiErrorCodes.PartialCheckoutNotAllowedWithCancelItems}.Message`)
      );
      return EMPTY;
    }

    return this.withBasketRecovery<ICart>(
      () => this._apiService.post(
        `${this._basePath}/add-item-to-basket`,
        {
          operationId: this._localStorageService.getItem('cash-key'),
          items,
          selectedPositions: this._paySeparatelyStore.getSelectedPositions()
        }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'addItem', items.map(x => x.itemId));
        this.pushCartError(res.message, notificationOptions);
      })
    );
  }

  public addConcessionItemToCart(
    item: Partial<ICartItem>,
    options?: { variablePrice?: boolean }
  ): Observable<IResponse<ICart>> {
    if (options?.variablePrice !== true) {
      const fCartItem = this._cartStore.cartItemsProductsAndPackages()
        .find(p => p.itemType === item.itemType && p.itemId === item.itemId);

      if (
        fCartItem &&
        (
          fCartItem.itemType === CartItemTypes.Product ||
          (
            fCartItem.itemType === CartItemTypes.Package &&
            fCartItem.packageProducts.length === 0
          )
        )
      ) {
        fCartItem.quantity += item.quantity;

        return this.updateCartItem([fCartItem]);
      }
    }

    return this.addItemToCart([item]);
  }

  public addFixedValueVoucherToCart(item: Partial<ICartItem>): Observable<IResponse<ICart>> {
    const vouchers = this._cartStore.cartItemsVouchers();
    const fCartItem = vouchers.find(
      v =>
        v.itemType === CartItemTypes.Voucher &&
        v.itemId === item.itemId &&
        (item.price == null || v.price === item.price)
    );

    if (fCartItem) {
      fCartItem.quantity += item.quantity ?? 1;
      return this.updateCartItem([fCartItem]);
    }

    return this.addItemToCart([item]);
  }

  public updateCartItem(items: ICartItem[]): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.patch(
        `${this._basePath}/update-basket-item`,
        {
          items,
          selectedPositions: this._paySeparatelyStore.getSelectedPositions()
        }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'updateItem', items.map(i => i.itemId));
        this.pushCartError(res.message);

        items.forEach((item) => {
          const hiddenItemsCount = this._paySeparatelyStore.getValue(item.itemId, item.position).length;

          if (hiddenItemsCount > 0) {
            this._paySeparatelyStore.removeKeysForCartPosition(item.itemId, item.position);

            for (let i = 0; i < hiddenItemsCount; i++) {
              this._paySeparatelyStore.togglePositionState(item, false, i);
            }
          }
        });
      })
    );
  }

  public clearCart(): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.patch(
        `${this._basePath}/clear-basket`,
        {
          operationId: this._localStorageService.getItem('cash-key'),
        }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.clearPaySeparatelyMode();
        this.triggerCartAction(res.data, 'clear');
        this.pushCartError(res.message);
      })
    );
  }

  public removePositionFromCart(itemId: number, position: number): Observable<IResponse<ICart>> {
    const cart = this._cartStore.cart();

    if (!cart) {
      return this.removeItemFromCart(this.getItemsToRemoveForPosition(itemId, position));
    }

    const fItem = cart.items.find(x => x.itemId === itemId && x.position === position);
    const hiddenCount = this._paySeparatelyStore.getValue(itemId, position).length;

    if (
      this._paySeparatelyStore.isPaySeparatelyModeEnabled() &&
      hiddenCount > 0 &&
      fItem
    ) {
      return this.updateCartItem([{ ...fItem, quantity: hiddenCount }]);
    }

    return this.removeItemFromCart(this.getItemsToRemoveForPosition(itemId, position));
  }

  private getItemsToRemoveForPosition(itemId: number, position: number): RemoveItemsFromCart {
    const cart = this._cartStore.cart();

    if (!cart) {
      return [{ itemId, position }];
    }

    const selectedItem = cart.items.find(x => x.itemId === itemId && x.position === position);

    if (!selectedItem) {
      return [{ itemId, position }];
    }

    const isCardItem =
      selectedItem.itemType === CartItemTypes.Card ||
      selectedItem.itemType === CartItemTypes.LoadCard ||
      selectedItem.itemType === CartItemTypes.CancelCard;

    if (!isCardItem || selectedItem.cardId == null) {
      return [{ itemId, position }];
    }

    const sameCardItems = cart.items.filter(
      x =>
        x.cardId != null &&
        x.cardId === selectedItem.cardId &&
        (x.itemType === CartItemTypes.Card ||
          x.itemType === CartItemTypes.LoadCard ||
          x.itemType === CartItemTypes.CancelCard)
    );

    return sameCardItems.map(x => ({ itemId: x.itemId, position: x.position }));
  }

  public removeItemFromCart(items: RemoveItemsFromCart): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.delete(
        `${this._basePath}/delete-basket-item`,
        {
          operationId: this._localStorageService.getItem('cash-key'),
          items,
          selectedPositions: this._paySeparatelyStore.getSelectedPositions()
        }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(
          res.data,
          'removeItem',
          items.map(x => x.itemId)
        );

        items.forEach(item => {
          this._paySeparatelyStore.removeKeysForCartPosition(item.itemId, item.position);
        });

        this.pushCartError(res.message);
      })
    );
  }

  public removePaymentFromCart(position: number): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.delete(
        `${this._basePath}/delete-basket-payment`,
        {
          position
        }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'removePayment');
        this.pushCartError(res.message);
      })
    );
  }

  public checkoutCart(
    customerName?: string,
    reservationCode?: string,
    description?: string,
    transactionRemark?: string,
    selectedPositions?: { itemId: number; position: number, quantity?: number }[],
    rebookTransactionId?: number,
    rebookShowId?: number,
  ): Observable<IResponse<ICartCheckout>> {
    return this.withBasketRecovery<ICartCheckout>(
      () => this._apiService.post(
        `${this._basePath}/checkout`,
        {
          customerName: customerName ?? null,
          reservationCode: reservationCode ?? null,
          cancelInfo: description ?? null,
          remark: transactionRemark ?? null,
          selectedPositions: this.normalizeSelectedPositions(selectedPositions),
          rebookTransactionId: rebookTransactionId ?? null,
          rebookShowId: rebookShowId ?? null
        }
      )
    ).pipe(
      tap((res: IResponse<ICartCheckout>) => {
        this.triggerCartAction(res.data.item.basket, 'checkout');
        this.pushCartError(res.message);
      })
    );
  }

  /**
   * Extends the seat holds ("noted" status) of the current basket on the server so they are not
   * released to concurrent online sales while a (potentially long) checkout is in progress.
   * Returns the seat ids that could no longer be refreshed (already freed or sold elsewhere).
   */
  public refreshSeatNotes(): Observable<IResponse<number[]>> {
    return this._apiService.post(`${this._basePath}/refresh-seat-notes`, {});
  }

  public addPaymentToCart(payment: ICartPayment): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.post(
        `${this._basePath}/add-payment`,
        {
          payment
        }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'addPayment');
        this.pushCartError(res.message);
      })
    );
  }

  public attachCardToCart(cardNumber: string, notificationOptions?: CartErrorNotificationOptions): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.post(
        `${this._basePath}/attach-card`,
        {
          cardNumber,
          selectedPositions: this._paySeparatelyStore.getSelectedPositions()
        }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'attachCard');
        this.pushCartError(res.message, notificationOptions);
      })
    );
  }

  public exchangeCard(
    oldCardNumber: string,
    newCardNumber: string,
    withFee: boolean
  ): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.post(
        `${this._basePath}/exchange-card`,
        {
          oldCardNumber,
          newCardNumber,
          withFee
        }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'addItem');
        this.pushCartError(res.message);
      })
    );
  }

  public detachCardFromCart(cardId?: number): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.delete(`${this._basePath}/detach-card`, cardId ? { cardId } : {})
    )
      .pipe(
        tap((res: IResponse<ICart>) => {
          this.triggerCartAction(res.data, 'detachCard');
          this.pushCartError(res.message);
        })
      );
  }

  public attachCinevillePartnerCode(seatId: number, position: number, partnerCode: string): Observable<IResponse<ICart>> {
    return this._apiService.post(
      `${this._basePath}/attach-cineville-partner-code`,
      { seatId, position, partnerCode: partnerCode.trim() }
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'attachCinevillePartnerCode', [seatId]);
        this.pushCartError(res.message);
      })
    );
  }

  public detachCinevillePartnerCode(seatId: number, position: number): Observable<IResponse<ICart>> {
    return this._apiService.delete(
      `${this._basePath}/detach-cineville-partner-code`,
      { seatId, position }
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'detachCinevillePartnerCode', [seatId]);
        this.pushCartError(res.message);
      })
    );
  }

  public applyEmployeePricesToCart(employeeCardCode?: string): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.post(
        `${this._basePath}/apply-employee-prices`,
        {
          ...(employeeCardCode ? { cardNumber: employeeCardCode } : {}),
          selectedPositions: this._paySeparatelyStore.getSelectedPositions()
        }
      )
    )
      .pipe(
        tap((res: IResponse<ICart>) => {
          this.triggerCartAction(res.data, 'applyEmployeePrices');
          this.pushCartError(res.message);
        })
      );
  }

  public undoLastCardLoad(cardNumber: string): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.post(
        `${this._basePath}/undo-last-card-load`,
        { cardNumber }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'addItem');
        this.pushCartError(res.message);
      })
    );
  }

  public withdrawCardBalance(cardNumber: string, amount: number): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.post(
        `${this._basePath}/withdraw-card-balance`,
        { cardNumber, amount }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'addItem');
        this.pushCartError(res.message);
      })
    );
  }

  public cancelCustomerCard(cardNumber: string): Observable<IResponse<ICart>> {
    return this._apiService.post(
      `${this._basePath}/cancel-customer-card`,
      { cardNumber }
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'addItem');
        this.pushCartError(res.message);
      })
    );
  }

  public applyFixedDiscountToCart(discountPercentage: number): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.post(
        `${this._basePath}/apply-fixed-discount`,
        {
          discountPercentage,
          selectedPositions: this._paySeparatelyStore.getSelectedPositions()
        }
      )
    )
      .pipe(
        tap((res: IResponse<ICart>) => {
          this.triggerCartAction(res.data, 'applyFixedDiscount');
          this.pushCartError(res.message);
        })
      );
  }

  public applyFixedDiscountWithAuthorizationToCart(
    email: string,
    pin: number,
    discountPercentage: number
  ): Observable<IResponse<ICart>> {
    return this._apiService.post(
      `${this._basePath}/apply-fixed-discount-with-authorization`,
      {
        email,
        pin,
        discountPercentage,
        selectedPositions: this._paySeparatelyStore.getSelectedPositions()
      }
    )
      .pipe(
        tap((res: IResponse<ICart>) => {
          this.triggerCartAction(res.data, 'applyFixedDiscount');
          this.pushCartError(res.message);
        })
      );
  }

  public removeAllDiscountsFromCart(): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.delete(
        `${this._basePath}/remove-all-discounts`,
        {
          selectedPositions: this._paySeparatelyStore.getSelectedPositions()
        }
      )
    )
      .pipe(
        tap((res: IResponse<ICart>) => {
          this.triggerCartAction(res.data, 'removeAllDiscountsFrom');
          this.pushCartError(res.message);
        })
      );
  }

  public applyDiscountCodeToCart(code: string): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.post(
        `${this._basePath}/apply-discount-code`,
        {
          code,
          selectedPositions: this._paySeparatelyStore.getSelectedPositions()
        }
      )
    )
      .pipe(
        tap((res: IResponse<ICart>) => {
          if (res.data?.discount?.type === DiscountType.DiscountCode) {
            this.clearPaySeparatelyMode();
          }
          this.triggerCartAction(res.data, 'applyDiscountCode');
          this.pushCartError(res.message);
        })
      );
  }

  public recalculateCineCardAmount(): Observable<IResponse<ICart>> {
    return this.withBasketRecovery<ICart>(
      () => this._apiService.post(
        `${this._basePath}/recalculate-cinecard-amount`,
        { selectedPositions: this._paySeparatelyStore.getSelectedPositions() }
      )
    ).pipe(
      tap((res: IResponse<ICart>) => {
        this.triggerCartAction(res.data, 'recalculateCineCardAmount');
        this.pushCartError(res.message);
      })
    );
  }

  public startCardPayment(amount: number): Observable<IResponse<ICardPaymentData>> {
    return this.devReaderId().pipe(
      switchMap(readerId => this.withBasketRecovery<ICardPaymentData>(
        () => this._apiService.post(
          `${this._basePath}/start-card-payment`,
          { amount, readerId: readerId || null }
        )
      ))
    );
  }

  private devReaderId(): Observable<string | null> {
    if (!environment.sumUpDevReader) {
      return of(null);
    }

    return of(SumUpDevReader.readerId() || null);
  }

  public startCardRefund(): Observable<IResponse<void>> {
    return this.withBasketRecovery<void>(
      () => this._apiService.post(
        `${this._basePath}/start-card-refund`,
        { amount: this._cartStore.cart().remainingAmount }
      )
    );
  }

  public triggerCartAction(cart: ICart, actionType: ICartAction['actionType'], itemIds: number[] = []): void {
    const cartAction: ICartAction = { actionType, itemIds };
    this._cartStore.cart.set(cart);
    this._cartStore.cartAction$.next(cartAction);

    this._broadcastChannelService.cashChannelPostMessage({
      type: BroadcastChannelMessageTypes.CART_CHANGES,
      data: {
        cart: this._cartStore.cart(),
        cartAction
      }
    });
  }

  private withBasketRecovery<T>(operationFactory: () => Observable<IResponse<T>>): Observable<IResponse<T>> {
    return operationFactory().pipe(
      catchError((error: unknown) => {
        const isBasketMissing = error instanceof HttpErrorResponse &&
          (error.status === 404 || error.error?.message === ApiErrorCodes.BasketNotFound);

        if (!isBasketMissing) {
          return throwError(() => error);
        }

        const cachedItems = this._cartStore.cart()?.items ?? [];

        return this.recreateBasket().pipe(
          switchMap(() => (cachedItems.length > 0 ? this.replayCachedItems(cachedItems) : of(null))),
          switchMap(() => operationFactory())
        );
      })
    );
  }

  private recreateBasket(): Observable<IResponse<ICart>> {
    return this._apiService.post(this._basePath)
      .pipe(
        tap((res: IResponse<ICart>) => {
          this._terminalsStore.selectedTerminal.update(terminal => ({
            ...terminal,
            currentSession: {
              ...terminal.currentSession,
              currentBasketId: res.data.id
            }
          }));
        })
      );
  }

  private replayCachedItems(items: ICartItem[]): Observable<IResponse<ICart>> {
    return this._apiService.post(
      `${this._basePath}/add-item-to-basket`,
      {
        operationId: this._localStorageService.getItem('cash-key'),
        items,
        selectedPositions: null
      }
    );
  }

  private pushCartError(message: string, notificationOptions?: CartErrorNotificationOptions): void {
    if (message === ApiErrorCodes.ShowDeleted) {
      this._showsService.handleShowDeleted().subscribe();
      return;
    }

    if (notificationOptions?.suppressedMessages?.includes(message)) {
      return;
    }

    if (message in ErrorCodes || message in ApiErrorCodes) {
      this._notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        message.trans(`errors/notifications/${message}.Message`)
      );
    }
  }

  private clearPaySeparatelyMode(): void {
    this._paySeparatelyStore.reset();
  }

  private normalizeSelectedPositions(
    selectedPositions?: { itemId: number; position: number, quantity?: number }[]
  ): { itemId: number; position: number, quantity?: number }[] | null {
    if (!selectedPositions || selectedPositions.length === 0) {
      return null;
    }

    const cart = this._cartStore.cart();
    if (!cart || !cart.items || cart.items.length === 0) {
      return null;
    }

    const allItemsCovered = cart.items.every(item => {
      const selectedPosition = selectedPositions.find(
        sp => sp.itemId === item.itemId && sp.position === item.position
      );

      if (!selectedPosition) {
        return false;
      }

      const selectedQuantity = selectedPosition.quantity ?? item.quantity;

      return selectedQuantity >= item.quantity;
    });

    return allItemsCovered ? null : selectedPositions;
  }
}
